/**
 * Build a list of FAQ Q&A pairs from a car's spec data.
 *
 * Why we generate this from car data instead of free-form authoring:
 *   - Every listing automatically gets useful, accurate FAQ content
 *     without admin effort.
 *   - The same source of truth (the `cars` row) feeds both the visible
 *     section and the FAQPage JSON-LD, so they can never drift apart —
 *     which is a Google policy requirement (FAQ structured data must
 *     mirror visible page content).
 *
 * The order is intentional: the first three are the questions buyers
 * almost always ask up front (price, mileage, fuel/transmission), then
 * trust signals (test drive, location, after-sale).
 */
import { CONTACT } from "@/config/contact";

export interface CarFaqInput {
  brand: string;
  model: string;
  variant?: string | null;
  year: number;
  mileage: number;
  price: number;
  fuel_type?: string | null;
  transmission?: string | null;
  horsepower?: number | null;
  body_type?: string | null;
  is_leasing?: boolean | null;
  leasing_price?: number | null;
}

export interface CarFaqEntry {
  question: string;
  /** Plain-text answer. Used directly in JSON-LD `acceptedAnswer.text`. */
  answer: string;
}

const dk = (n: number) => new Intl.NumberFormat("da-DK").format(n);

function fuelLabel(fuel?: string | null): string {
  if (!fuel) return "ikke angivet";
  const map: Record<string, string> = {
    benzin: "benzin",
    diesel: "diesel",
    el: "el",
    hybrid: "hybrid",
    plugin_hybrid: "plug-in hybrid",
  };
  return map[fuel] ?? fuel;
}

function transmissionLabel(t?: string | null): string {
  if (!t) return "ikke angivet";
  return t === "automatik" ? "automatgear" : "manuelt gear";
}

export function buildCarFaq(car: CarFaqInput): CarFaqEntry[] {
  const carName = `${car.brand} ${car.model}${car.variant ? " " + car.variant : ""}`;
  const fullName = `${carName} fra ${car.year}`;
  const fuel = fuelLabel(car.fuel_type);
  const trans = transmissionLabel(car.transmission);

  const faqs: CarFaqEntry[] = [
    {
      question: `Hvad koster ${carName}?`,
      answer: car.is_leasing && car.leasing_price && car.leasing_price > 0
        ? `Kontantprisen for ${fullName} er ${dk(car.price)} kr. Bilen kan også leases fra ${dk(car.leasing_price)} kr/md. Ring til os på ${CONTACT.phoneDisplay} for et personligt finansieringstilbud.`
        : `${fullName} koster ${dk(car.price)} kr. Vi tilbyder også finansiering — ring til os på ${CONTACT.phoneDisplay} for et tilbud tilpasset din situation.`,
    },
    {
      question: `Hvor mange kilometer har bilen kørt?`,
      answer: `${fullName} har ${dk(car.mileage)} km på speedometeret. Servicebog og historik gennemgås altid sammen med dig inden handel.`,
    },
    {
      question: `Hvilket brændstof og gearkasse har ${carName}?`,
      answer: car.horsepower
        ? `Bilen kører på ${fuel} og har ${trans}. Motoren yder ${car.horsepower} HK.`
        : `Bilen kører på ${fuel} og har ${trans}.`,
    },
    {
      question: `Kan jeg booke en prøvetur?`,
      answer: `Ja. Brug knappen "Book prøvetur" øverst på siden, eller ring til os på ${CONTACT.phoneDisplay}. Du er også velkommen til at aftale et tidspunkt uden for normal åbningstid.`,
    },
    {
      question: `Hvor kan jeg se bilen?`,
      answer: `Du finder ${carName} hos ${CONTACT.companyName}, ${CONTACT.addressLine}. Åbningstider er mandag–fredag kl. 09–16, og weekender efter aftale.`,
    },
    {
      question: `Får jeg garanti og service med?`,
      answer: `Alle vores biler gennemgås grundigt inden salg. Vi tilbyder garanti og hjælper gerne med finansiering, forsikring og bytte af din nuværende bil. Kontakt os på ${CONTACT.email} eller ${CONTACT.phoneDisplay} for detaljer på denne specifikke bil.`,
    },
  ];

  return faqs;
}

/**
 * Serialize FAQ entries as schema.org `FAQPage` JSON-LD.
 *
 * Returns a stringified JSON-LD object ready to drop into a
 * `<script type="application/ld+json">` block.
 */
export function buildCarFaqJsonLd(car: CarFaqInput): string {
  const faqs = buildCarFaq(car);
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  });
}
